import { useCallback, useEffect, useRef, useState } from 'react';
import { Baseline, Bold, Highlighter, Italic, RemoveFormatting, Type } from 'lucide-react';
import { BRAND, TEXT_FARBEN, TEXT_GROESSEN, type TextFarbe, type TextGroesse } from '../../lib/mailBuilder/render';
import { MARK_HEX, editorZuMarkup, markupZuEditorHtml } from './umwandeln';
import './TextEditor.css';

type Props = {
  id: string;
  /** Gespeicherter Text mit Formatierungszeichen */
  value: string;
  onChange: (wert: string) => void;
  placeholder?: string;
  zeilen?: number;
  invalid?: boolean;
  required?: boolean;
  labelledBy?: string;
  describedBy?: string;
};

const FONT_GROESSE: Record<TextGroesse | 'normal', string> = { klein: '2', normal: '3', gross: '5' };

/**
 * Textfeld mit Formatierungsleiste: fett, kursiv, markiert, Farben und Größen
 * — nur aus dem Brand-Design. Eingefügter Text kommt ohne fremde
 * Formatierung an. Tastatur: Strg+B, Strg+I; die Leiste ist per Tab
 * erreichbar.
 */
export function TextEditor({ id, value, onChange, placeholder, zeilen = 5, invalid, required, labelledBy, describedBy }: Props) {
  const feld = useRef<HTMLDivElement>(null);
  const letzter = useRef<string | null>(null);
  const auswahl = useRef<Range | null>(null);
  const [aktiv, setAktiv] = useState({ b: false, i: false, mark: false });
  const [menue, setMenue] = useState<'farbe' | 'groesse' | null>(null);
  const [leer, setLeer] = useState(!value);

  // Wert von außen (Laden, andere Vorlage) übernehmen — eigene Eingaben nicht überschreiben.
  useEffect(() => {
    const el = feld.current;
    if (!el || value === letzter.current) return;
    el.innerHTML = markupZuEditorHtml(value);
    letzter.current = value;
    setLeer(!value);
  }, [value]);

  const zustandLesen = useCallback(() => {
    try {
      const hg = String(document.queryCommandValue('hiliteColor') || document.queryCommandValue('backColor') || '');
      setAktiv({
        b: document.queryCommandState('bold'),
        i: document.queryCommandState('italic'),
        mark: /212,\s*201,\s*181/.test(hg) || hg.toLowerCase() === MARK_HEX.toLowerCase(),
      });
    } catch { /* ältere Browser */ }
  }, []);

  // Auswahl merken, damit ein Klick in die Leiste sie nicht verliert.
  useEffect(() => {
    const merken = () => {
      const sel = window.getSelection();
      const el = feld.current;
      if (!sel || sel.rangeCount === 0 || !el) return;
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) {
        auswahl.current = r.cloneRange();
        zustandLesen();
      }
    };
    document.addEventListener('selectionchange', merken);
    return () => document.removeEventListener('selectionchange', merken);
  }, [zustandLesen]);

  const melden = () => {
    const el = feld.current;
    if (!el) return;
    const m = editorZuMarkup(el);
    setLeer(!m);
    if (m === letzter.current) return;
    letzter.current = m;
    onChange(m);
  };

  const auswahlHerstellen = () => {
    const el = feld.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && auswahl.current) {
      sel.removeAllRanges();
      sel.addRange(auswahl.current);
    }
  };

  const befehl = (cmd: string, arg?: string) => {
    auswahlHerstellen();
    try {
      document.execCommand('styleWithCSS', false, 'false');
      if (cmd === 'hiliteColor' && !document.execCommand('hiliteColor', false, arg)) document.execCommand('backColor', false, arg);
      else if (cmd !== 'hiliteColor') document.execCommand(cmd, false, arg);
    } catch { /* nicht unterstützt */ }
    setMenue(null);
    melden();
    zustandLesen();
  };

  const markieren = () => befehl('hiliteColor', aktiv.mark ? 'transparent' : MARK_HEX);

  const einfuegen = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const taste = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && menue) { setMenue(null); e.stopPropagation(); }
  };

  const hoehe = `${Math.max(3, zeilen) * 1.6 + 1.2}em`;

  return (
    <div className={`te${invalid ? ' te--fehler' : ''}`} onKeyDown={taste}>
      <div className="te-leiste" role="toolbar" aria-label="Text formatieren" aria-controls={id}>
        <button type="button" className={`te-knopf${aktiv.b ? ' aktiv' : ''}`} aria-pressed={aktiv.b} title="Fett (Strg+B)" aria-label="Fett"
          onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('bold')}>
          <Bold size={15} aria-hidden="true" />
        </button>
        <button type="button" className={`te-knopf${aktiv.i ? ' aktiv' : ''}`} aria-pressed={aktiv.i} title="Kursiv (Strg+I)" aria-label="Kursiv"
          onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('italic')}>
          <Italic size={15} aria-hidden="true" />
        </button>
        <button type="button" className={`te-knopf${aktiv.mark ? ' aktiv' : ''}`} aria-pressed={aktiv.mark} title="Markieren wie mit dem Textmarker" aria-label="Markieren"
          onMouseDown={(e) => e.preventDefault()} onClick={markieren}>
          <Highlighter size={15} aria-hidden="true" />
        </button>
        <span className="te-trenner" aria-hidden="true" />
        <div className="te-menue-anker">
          <button type="button" className={`te-knopf te-knopf--text${menue === 'farbe' ? ' aktiv' : ''}`} aria-haspopup="menu" aria-expanded={menue === 'farbe'}
            title="Schriftfarbe" onMouseDown={(e) => e.preventDefault()} onClick={() => setMenue(menue === 'farbe' ? null : 'farbe')}>
            <Baseline size={15} aria-hidden="true" /> Farbe
          </button>
          {menue === 'farbe' && (
            <div className="te-menue" role="menu" aria-label="Schriftfarbe">
              <button type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('foreColor', BRAND.ink)}>
                <span className="te-farbe" style={{ background: BRAND.ink }} aria-hidden="true" /> Standard
              </button>
              {(Object.keys(TEXT_FARBEN) as TextFarbe[]).map((f) => (
                <button key={f} type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('foreColor', TEXT_FARBEN[f].hex)}>
                  <span className="te-farbe" style={{ background: TEXT_FARBEN[f].hex }} aria-hidden="true" /> {TEXT_FARBEN[f].label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="te-menue-anker">
          <button type="button" className={`te-knopf te-knopf--text${menue === 'groesse' ? ' aktiv' : ''}`} aria-haspopup="menu" aria-expanded={menue === 'groesse'}
            title="Schriftgröße" onMouseDown={(e) => e.preventDefault()} onClick={() => setMenue(menue === 'groesse' ? null : 'groesse')}>
            <Type size={15} aria-hidden="true" /> Größe
          </button>
          {menue === 'groesse' && (
            <div className="te-menue" role="menu" aria-label="Schriftgröße">
              {(['klein', 'normal', 'gross'] as const).map((g) => (
                <button key={g} type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('fontSize', FONT_GROESSE[g])}>
                  <span className={`te-groesse te-groesse--${g}`}>{g === 'normal' ? 'Normal' : TEXT_GROESSEN[g].label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="te-trenner" aria-hidden="true" />
        <button type="button" className="te-knopf" title="Formatierung entfernen" aria-label="Formatierung entfernen"
          onMouseDown={(e) => e.preventDefault()} onClick={() => befehl('removeFormat')}>
          <RemoveFormatting size={15} aria-hidden="true" />
        </button>
      </div>
      <div
        ref={feld}
        id={id}
        className={`te-feld${leer ? ' te-feld--leer' : ''}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        data-placeholder={placeholder}
        style={{ minHeight: hoehe }}
        onInput={melden}
        onBlur={melden}
        onPaste={einfuegen}
        onDrop={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
        onKeyUp={zustandLesen}
        onMouseUp={zustandLesen}
      />
    </div>
  );
}
